import { ConnectedPosition, CdkOverlayOrigin, CdkConnectedOverlay } from '@angular/cdk/overlay';
import {
  Component,
  ViewEncapsulation,
  forwardRef,
  ChangeDetectorRef,
  ElementRef,
  inject,
  input,
  signal,
  computed,
  viewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { InputNumberDirective } from '../input-number.directive';

export interface ComboboxOption {
  label: string;
  value: number;
}

@Component({
  selector: 'app-combobox',
  templateUrl: './combobox.component.html',
  styleUrls: ['./combobox.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ComboboxComponent),
      multi: true
    }
  ],
  imports: [CdkOverlayOrigin, CdkConnectedOverlay, FormsModule, InputNumberDirective]
})
export class ComboboxComponent implements ControlValueAccessor {
  private static nextId = 0;
  private cdr = inject(ChangeDetectorRef);
  private readonly instanceId = `if-combobox-${ComboboxComponent.nextId++}`;
  readonly listboxId = `${this.instanceId}-listbox`;

  suggestions = input<ComboboxOption[]>([]);
  type = input<'text' | 'number'>('number');
  disabled = input(false);
  placeholder = input('');
  min = input<number | undefined>(undefined);
  buttonLabel = input<string | undefined>(undefined);
  suffix = input<string>('');
  suffixHint = input<string>('');
  /** Accessible name for the text input; there is no visible `<label>` bound to it. */
  ariaLabel = input<string | null>(null);

  originElement = viewChild<ElementRef>('trigger');
  inputElement = viewChild<ElementRef>('inputEl');

  _disabledByCva = signal(false);
  isDisabled = computed(() => this.disabled() || this._disabledByCva());

  private _value: number | string = '';
  private _displayValue: string = '';
  isOpen = false;
  highlightedIndex = -1;
  /** The active suggestion was reached with the keyboard, so it shows the ring. */
  keyboardActive = false;

  private onChange = (_value: number | string) => {};
  private onTouched = () => {};

  get originWidth(): number {
    const el = this.originElement();
    return el?.nativeElement?.offsetWidth || 0;
  }

  get displayValue(): string {
    return this._displayValue;
  }

  optionId(index: number): string {
    return `${this.instanceId}-option-${index}`;
  }

  get activeDescendant(): string | null {
    return this.isOpen && this.highlightedIndex >= 0 && this.highlightedIndex < this.suggestions().length
      ? this.optionId(this.highlightedIndex)
      : null;
  }

  overlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' }
  ];

  writeValue(value: number | string): void {
    this._value = value ?? '';
    this._displayValue = value != null ? String(value) : '';
    this.cdr.detectChanges();
  }

  registerOnChange(fn: (value: number | string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabledByCva.set(isDisabled);
    this.cdr.detectChanges();
  }

  onInput(e: Event): void {
    const target = e.target as HTMLInputElement;
    const raw = target.value;
    this._displayValue = raw;

    if (this.type() === 'number') {
      const parsed = parseFloat(raw);
      const numVal = isNaN(parsed) ? 0 : parsed;
      if (numVal !== this._value) {
        this._value = numVal;
        this.onChange(numVal);
      }
    } else {
      if (raw !== this._value) {
        this._value = raw;
        this.onChange(raw);
      }
    }
  }

  onBlur(): void {
    this.onTouched();
  }

  toggleDropdown(): void {
    if (this.isDisabled()) return;
    this.isOpen = !this.isOpen;
    this.highlightedIndex = -1;
  }

  openDropdown(): void {
    if (this.isDisabled() || this.isOpen) return;
    this.isOpen = true;
    this.highlightedIndex = -1;
  }

  closeDropdown(): void {
    this.isOpen = false;
    this.highlightedIndex = -1;
    this.keyboardActive = false;
  }

  selectOption(option: ComboboxOption): void {
    if (this.isDisabled()) return;
    this._value = option.value;
    this._displayValue = String(option.value);
    this.onChange(option.value);
    this.closeDropdown();
    this.inputElement()?.nativeElement?.focus();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (!this.isOpen) {
      // Alt+ArrowDown or ArrowDown opens the suggestions, as a native list does.
      if (event.key === 'ArrowDown' && this.suggestions().length > 0 && !this.isDisabled()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.openDropdown();
        this.highlightedIndex = 0;
        this.keyboardActive = true;
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        event.stopImmediatePropagation();
        this.highlightedIndex = (this.highlightedIndex + 1) % this.suggestions().length;
        this.keyboardActive = true;
        break;
      case 'ArrowUp':
        event.preventDefault();
        event.stopImmediatePropagation();
        {
          const len = this.suggestions().length;
          this.highlightedIndex = this.highlightedIndex <= 0 ? len - 1 : this.highlightedIndex - 1;
          this.keyboardActive = true;
        }
        break;
      case 'Enter':
        event.preventDefault();
        if (this.highlightedIndex >= 0 && this.highlightedIndex < this.suggestions().length) {
          this.selectOption(this.suggestions()[this.highlightedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.closeDropdown();
        break;
    }
  }
}
